/**
 * Pure helpers + display constants for the Plans view family.
 *
 * Phase A is INERT — the plan catalog defines limits + assigns
 * subscriptions, but the limits aren't enforced and the assignments
 * aren't billed. Every helper here is a string formatter or a closed
 * enum mapping; no API calls, no side effects.
 */

import type {
  AdminPlan,
  AdminPlanDetail,
  AdminPlanDimension,
  AdminPlanEntitlement,
  AdminPlanEntitlementBehavior,
  AdminPlanFeature,
  AdminPlanInterval,
  AdminPlanPrice,
  AdminPlanScope,
  AdminPlanStatus,
  AdminSubscriptionStatus,
} from '@sill/types/api';

import type { PillTone } from '../../components/atoms/Pill.js';

/** The four dimensions Sill exposes in Phase A. Adding a new dimension
 *  requires both a CHECK migration and a deliberate code change — this
 *  array is the single source of truth the editor / overrides surfaces
 *  iterate. Order is the display order (sites first because it's the
 *  one staff intuit immediately; the metered three follow). */
export const ALL_DIMENSIONS: readonly AdminPlanDimension[] = [
  'sites',
  'transactions_monthly',
  'agent_traffic_monthly',
  'red_team_runs_monthly',
  'retention_days',
];

/** Human-readable label for each dimension (kept short — these render
 *  inside form labels, table column headers, and entitlement rows).
 *
 *  ADR-0263 §2.3 widened `AdminPlanDimension` with six v2 members.
 *  The S1 admin UI (apps/admin) does NOT iterate the v2 set — the
 *  Plans view's `ALL_DIMENSIONS` constant below still ships the v1
 *  five — but the switch must stay exhaustive. The v2 branches
 *  return labels pulled from draft-5 so a future UI slice can render
 *  them without changing this file. */
export function dimensionLabel(d: AdminPlanDimension): string {
  switch (d) {
    case 'sites':
      return 'Sites';
    case 'transactions_monthly':
      return 'Transactions / month';
    case 'agent_traffic_monthly':
      return 'Agent traffic / month';
    case 'red_team_runs_monthly':
      return 'Red-team runs / month';
    case 'retention_days':
      return 'History (days)';
    case 'page_loads_monthly':
      return 'Page loads a month';
    case 'orders_monthly':
      return 'Agent orders a month';
    case 'assistant_requests_monthly':
      return 'AI assistant requests a month';
    case 'free_sites':
      return 'Free sites';
    case 'included_sites':
      return 'Included sites';
    case 'agency_scans_monthly':
      return 'Agency scans a month';
  }
}

/** One-line description used as a `<Field hint>` under each entitlement
 *  input. Keep terse — the column is narrow. */
export function dimensionHint(d: AdminPlanDimension): string {
  switch (d) {
    case 'sites':
      return 'Max sites the account can register. Blank = unlimited.';
    case 'transactions_monthly':
      return 'Settled mandate count per calendar month. Blank = unlimited.';
    case 'agent_traffic_monthly':
      return 'Agent identifications per calendar month. Blank = unlimited.';
    case 'red_team_runs_monthly':
      return 'Policy red-team runs per calendar month. Blank = unlimited.';
    case 'retention_days':
      return 'How far back reporting reaches, in days. Blank = unlimited history.';
    case 'page_loads_monthly':
      return 'Page loads per site, per month. Blank = unlimited.';
    case 'orders_monthly':
      return 'Completed agent orders per site, per month. Blank = unlimited.';
    case 'assistant_requests_monthly':
      return 'AI assistant requests. Blank = unlimited.';
    case 'free_sites':
      return 'Sites that may sit on the Free plan at no charge. Blank = unlimited.';
    case 'included_sites':
      return 'Sites an agency plan includes. Blank = unlimited.';
    case 'agency_scans_monthly':
      return 'Agency scans + invites per month. Blank = unlimited.';
  }
}

/** Plan-status → Pill tone. Closed enum so a typo can't widen. */
export function planStatusTone(status: AdminPlanStatus): PillTone {
  switch (status) {
    case 'draft':
      return 'sky';
    case 'active':
      return 'mint';
    case 'archived':
      return 'neutral';
  }
}

/** Subscription-status → Pill tone. */
export function subscriptionStatusTone(status: AdminSubscriptionStatus): PillTone {
  switch (status) {
    case 'active':
      return 'mint';
    case 'trialing':
      return 'sky';
    case 'past_due':
      return 'amber';
    case 'suspended':
    case 'canceled':
    case 'closed':
      return 'coral';
  }
}

export const SUBSCRIPTION_STATUS_OPTIONS: readonly AdminSubscriptionStatus[] = [
  'active',
  'trialing',
  'past_due',
  'suspended',
  'canceled',
  'closed',
];

export const PLAN_INTERVALS: readonly AdminPlanInterval[] = ['monthly', 'yearly'];

export const BEHAVIOR_OPTIONS: readonly AdminPlanEntitlementBehavior[] = [
  'hard',
  'soft',
];

// ---------------------------------------------------------------------------
// ADR-0263 v2 — scope maps, soft-only set, feature catalog.
//
// These MIRROR packages/entitlements/src/resolve.ts (DIMENSION_SCOPE,
// SOFT_ONLY_DIMENSIONS, FEATURES, FEATURE_SCOPE). apps/admin does not
// depend on @sill/entitlements today (check its package.json). If
// that package is added as a dep, replace these mirrors with direct
// imports from '@sill/entitlements'. The write-path server already
// uses the canonical source — these mirrors are UI gating only.
// ---------------------------------------------------------------------------

/** Dimension scope mirror of DIMENSION_SCOPE in @sill/entitlements. */
export const DIMENSION_SCOPE_MIRROR: Readonly<
  Record<AdminPlanDimension, 'site' | 'account' | 'both'>
> = {
  // v1 — one plan per account.
  sites: 'account',
  transactions_monthly: 'account',
  agent_traffic_monthly: 'account',
  red_team_runs_monthly: 'account',
  retention_days: 'both',
  // v2 (ADR-0263 §2.3).
  page_loads_monthly: 'site',
  orders_monthly: 'site',
  assistant_requests_monthly: 'site',
  free_sites: 'account',
  included_sites: 'account',
  agency_scans_monthly: 'account',
};

/** Soft-only dimensions (ADR-0263 §2.3). Hard rejected by the write
 *  path; the UI renders a fixed "Soft · never blocks" label. */
export const SOFT_ONLY_DIMENSIONS_MIRROR: ReadonlySet<AdminPlanDimension> = new Set([
  'page_loads_monthly',
  'orders_monthly',
  'assistant_requests_monthly',
]);

/** All feature slugs, in display order (site features first). */
export const FEATURES_MIRROR: readonly AdminPlanFeature[] = [
  'blocking',
  'custom_attack_paths',
  'slack_held_alerts',
  'assistant_blocking',
  'white_label',
  'agency_portal',
];

/** Feature scope mirror of FEATURE_SCOPE in @sill/entitlements. */
export const FEATURE_SCOPE_MIRROR: Readonly<Record<AdminPlanFeature, 'site' | 'account'>> = {
  blocking: 'site',
  custom_attack_paths: 'site',
  slack_held_alerts: 'site',
  assistant_blocking: 'site',
  white_label: 'account',
  agency_portal: 'account',
};

/** Short display name for each feature. */
export function featureLabel(f: AdminPlanFeature): string {
  switch (f) {
    case 'blocking':
      return 'Address and attack-campaign blocking';
    case 'custom_attack_paths':
      return 'Custom attack paths';
    case 'slack_held_alerts':
      return 'Held-order alerts in Slack';
    case 'assistant_blocking':
      return 'Assistant can create blocks';
    case 'white_label':
      return 'White-label reports';
    case 'agency_portal':
      return 'Agency Portal';
  }
}

/** One-line plain description (from the slice brief). */
export function featureHint(f: AdminPlanFeature): string {
  switch (f) {
    case 'blocking':
      return 'Turn on address and campaign blocking. Runs in the Sill Cloudflare Worker.';
    case 'custom_attack_paths':
      return 'Add up to 50 paths of your own on top of the built-in list.';
    case 'slack_held_alerts':
      return 'Post held orders to a Slack channel as well as email.';
    case 'assistant_blocking':
      return 'A connected Claude or ChatGPT may create 24-hour and 7-day blocks.';
    case 'white_label':
      return 'Reports and the readiness widget carry the agency’s brand.';
    case 'agency_portal':
      return 'Manage client accounts from the Agency Portal.';
  }
}

/** Dimensions a plan of a given scope may carry. `'both'` dimensions
 *  show on either scope (retention_days). */
export function dimensionsForScope(scope: AdminPlanScope): readonly AdminPlanDimension[] {
  return ALL_V2_DIMENSIONS.filter((d) => {
    const dim_scope = DIMENSION_SCOPE_MIRROR[d];
    return dim_scope === scope || dim_scope === 'both';
  });
}

/** Features a plan of a given scope may carry. */
export function featuresForScope(scope: AdminPlanScope): readonly AdminPlanFeature[] {
  return FEATURES_MIRROR.filter((f) => FEATURE_SCOPE_MIRROR[f] === scope);
}

/** All dimensions a v2 plan editor may expose (v1 dimensions excluded
 *  for v2 plans; v1 plans keep ALL_DIMENSIONS). */
export const ALL_V2_DIMENSIONS: readonly AdminPlanDimension[] = [
  'page_loads_monthly',
  'orders_monthly',
  'assistant_requests_monthly',
  'retention_days',
  'free_sites',
  'included_sites',
  'agency_scans_monthly',
];

/** Group a plan list into v2 site, v2 account, and legacy (v1). */
export interface GroupedPlans {
  readonly siteV2: readonly AdminPlanDetail[];
  readonly accountV2: readonly AdminPlanDetail[];
  readonly legacy: readonly AdminPlanDetail[];
}

export function groupPlans(plans: readonly AdminPlanDetail[]): GroupedPlans {
  const siteV2: AdminPlanDetail[] = [];
  const accountV2: AdminPlanDetail[] = [];
  const legacy: AdminPlanDetail[] = [];
  for (const p of plans) {
    if (p.plan.catalog_version === 2 && p.plan.scope === 'site') {
      siteV2.push(p);
    } else if (p.plan.catalog_version === 2 && p.plan.scope === 'account') {
      accountV2.push(p);
    } else {
      legacy.push(p);
    }
  }
  return { siteV2, accountV2, legacy };
}

/** 10× monthly minor-unit value for the "two months free" helper. */
export function tenXMonthly(monthlyMinor: string): string {
  const trimmed = monthlyMinor.trim();
  if (trimmed.length === 0) return '';
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) return '';
  return String(n * 10);
}

/** Scope label for the plan row chip. */
export function scopeLabel(scope: AdminPlanScope): string {
  return scope === 'site' ? 'Per site' : 'Account';
}

/** Does the plan tree (`AdminPlan`) require v2 UI treatment? */
export function isV2(plan: AdminPlan): boolean {
  return plan.catalog_version === 2;
}

/** Format a single price as `$10.00 / mo` or `$96.00 / yr`. Returns
 *  an em-dash when the price is absent. Works for plan + add-on prices
 *  because both share the same `interval` / `amount_minor` / `currency`
 *  surface. */
export function formatPriceLabel(
  price:
    | {
        readonly interval: AdminPlanInterval;
        readonly amount_minor: number;
        readonly currency: string;
      }
    | undefined,
): string {
  if (price === undefined) return '—';
  return `${formatPriceAmount(price.amount_minor, price.currency)} / ${
    price.interval === 'monthly' ? 'mo' : 'yr'
  }`;
}

function formatPriceAmount(amount_minor: number, currency: string): string {
  try {
    const major = amount_minor / 100;
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(major);
  } catch {
    return `${amount_minor} ${currency}`;
  }
}

/** Find a price row by interval, or `undefined`. Works for either
 *  `AdminPlanPrice` or `AdminPlanAddonPrice` — both carry the same
 *  `interval` + `amount_minor` + `currency` shape `formatPriceLabel`
 *  reads. */
export function findPrice<
  P extends {
    readonly interval: AdminPlanInterval;
    readonly amount_minor: number;
    readonly currency: string;
  },
>(prices: readonly P[], interval: AdminPlanInterval): P | undefined {
  return prices.find((p) => p.interval === interval);
}

/** Format an entitlement's `limit_value` for compact display. `null`
 *  → "Unlimited" (the contract: NULL = unlimited). */
export function formatLimit(limit: number | null): string {
  if (limit === null) return 'Unlimited';
  try {
    return limit.toLocaleString('en-US');
  } catch {
    return String(limit);
  }
}

/** Compact one-line summary of a plan's entitlements for the catalog
 *  table row, e.g. "Sites 3 · Tx 1,000 · Agents Unl · RT 10". */
export function summarizeEntitlements(
  entitlements: readonly AdminPlanEntitlement[],
): string {
  if (entitlements.length === 0) return 'No limits defined';
  const byDim = new Map<AdminPlanDimension, AdminPlanEntitlement>();
  for (const e of entitlements) byDim.set(e.dimension, e);
  const parts: string[] = [];
  for (const d of [...ALL_DIMENSIONS, ...ALL_V2_DIMENSIONS.filter((x) => x !== 'retention_days')]) {
    const e = byDim.get(d);
    if (e === undefined) continue;
    parts.push(`${shortDimensionLabel(d)} ${formatLimit(e.limit_value)}`);
  }
  return parts.join(' · ');
}

function shortDimensionLabel(d: AdminPlanDimension): string {
  switch (d) {
    case 'sites':
      return 'Sites';
    case 'transactions_monthly':
      return 'Tx';
    case 'agent_traffic_monthly':
      return 'Agents';
    case 'red_team_runs_monthly':
      return 'RT';
    case 'retention_days':
      return 'History';
    // ADR-0263 §2.3 — v2 dimensions, shown in the catalog row too.
    case 'page_loads_monthly':
      return 'Loads';
    case 'orders_monthly':
      return 'Orders';
    case 'assistant_requests_monthly':
      return 'Asst';
    case 'free_sites':
      return 'Free';
    case 'included_sites':
      return 'Incl';
    case 'agency_scans_monthly':
      return 'Scans';
  }
}
